import React, { useState, useEffect, useMemo } from 'react';
import chemicalService from '../../../services/chemicalService';

const PAYMENT_MODES = ['cash', 'cheque'];

const PaymentFormModal = ({ defaultType, onClose, onSaved }) => {
  // ─── Type & party master data ──
  const [type, setType] = useState(defaultType || 'receipt'); // 'receipt' | 'payment'
  const [parties, setParties] = useState([]);
  const [loadingParties, setLoadingParties] = useState(false);

  const [invoices, setInvoices] = useState([]); // sales for receipts, purchases for payments
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  const [form, setForm] = useState({
    party_id: '',
    against_id: '',
    date: new Date().toISOString().split('T')[0],
    amount: '',
    wht_percent: '',
    wht_amount: '',
    bank_name: '',
    cheque_number: '',
    notes: '',
  });

  const [submitting, setSubmitting] = useState(false);

  // ─── Load parties when type changes ──
  useEffect(() => {
    const loadParties = async () => {
      try {
        setLoadingParties(true);
        // Reset downstream state
        setForm((prev) => ({ ...prev, party_id: '', against_id: '', amount: '', wht_percent: '', wht_amount: '' }));
        setInvoices([]);

        const fetcher = type === 'receipt'
          ? chemicalService.getCustomers
          : chemicalService.getVendors;
        const res = await fetcher({ page: 1, limit: 1000 });
        // Show only parties with outstanding > 0
        const filtered = (res.data || []).filter(
          (p) => p.is_active && parseFloat(p.outstanding_balance) > 0
        );
        setParties(filtered);
      } catch (err) {
        console.error('Error loading parties:', err);
        alert('❌ Failed to load ' + (type === 'receipt' ? 'customers' : 'vendors'));
      } finally {
        setLoadingParties(false);
      }
    };
    loadParties();
  }, [type]);

  // ─── Load invoices when party changes ──
  useEffect(() => {
    if (!form.party_id) {
      setInvoices([]);
      return;
    }

    const loadInvoices = async () => {
      try {
        setLoadingInvoices(true);
        setForm((prev) => ({ ...prev, against_id: '', amount: '', wht_percent: '', wht_amount: '' }));

        const fetcher = type === 'receipt'
          ? chemicalService.getSales
          : chemicalService.getPurchases;

        // Filter by party + non-paid status
        const params = type === 'receipt'
          ? { customer_id: form.party_id, page: 1, limit: 1000 }
          : { vendor_id: form.party_id, page: 1, limit: 1000 };

        const res = await fetcher(params);
        const unsettled = (res.data || []).filter(
          (inv) => inv.payment_status !== 'paid'
        );
        setInvoices(unsettled);
      } catch (err) {
        console.error('Error loading invoices:', err);
        alert('❌ Failed to load invoices/bills');
      } finally {
        setLoadingInvoices(false);
      }
    };
    loadInvoices();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.party_id]);

  // ─── Computed: selected invoice + remaining due ──
  const { selectedInvoice, remainingDue } = useMemo(() => {
    const inv = invoices.find((i) => i.id === parseInt(form.against_id, 10));
    if (!inv) return { selectedInvoice: null, remainingDue: 0 };
    const remaining = parseFloat(inv.grand_total) - parseFloat(inv.amount_paid);
    return { selectedInvoice: inv, remainingDue: remaining };
  }, [invoices, form.against_id]);

  // ─── Auto-fill amount = remaining due when invoice picked ──
  useEffect(() => {
    if (selectedInvoice && !form.amount) {
      setForm((prev) => ({
        ...prev,
        amount: remainingDue.toFixed(2).replace(/\.?0+$/, ''),
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInvoice?.id]);

  // ─── WHT auto-calculation ──
  const whtPercent = parseFloat(form.wht_percent) || 0;
  const cashAmount = parseFloat(form.amount) || 0;

  // WHT is calculated on the remaining due (the settlement base), not on the cash amount
  // totalSettlement = remaining due (or the amount being settled)
  // wht = totalSettlement * wht% / 100
  // cash = totalSettlement - wht
  // So when user enters WHT %, we recalculate: wht_amount = remainingDue * wht% / 100, cash = remainingDue - wht_amount
  const handleWhtChange = (percent) => {
    const pct = parseFloat(percent) || 0;
    if (pct < 0 || pct > 100) return;
    const whtAmt = Math.round(remainingDue * pct / 100 * 100) / 100;
    const cash = Math.round((remainingDue - whtAmt) * 100) / 100;
    setForm((prev) => ({
      ...prev,
      wht_percent: percent,
      wht_amount: whtAmt > 0 ? String(whtAmt) : '',
      amount: cash > 0 ? String(cash) : '',
    }));
  };

  const whtAmount = parseFloat(form.wht_amount) || 0;
  const totalSettlement = cashAmount + whtAmount;

  // ─── Handlers ──
  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.party_id) return alert(`❌ Select a ${type === 'receipt' ? 'customer' : 'vendor'}.`);
    if (!form.against_id) return alert(`❌ Select an ${type === 'receipt' ? 'invoice' : 'bill'}.`);
    if (!form.amount || parseFloat(form.amount) <= 0) return alert('❌ Cash amount must be > 0.');
    if (totalSettlement > remainingDue + 0.01) {
      return alert(`❌ Total settlement (cash + WHT = Rs ${formatNumber(totalSettlement)}) exceeds remaining due (Rs ${formatNumber(remainingDue)}).`);
    }
    if (!form.date) return alert('❌ Date is required.');

    try {
      setSubmitting(true);
      const payload = {
        type,
        date: form.date,
        party_type: type === 'receipt' ? 'customer' : 'vendor',
        party_id: parseInt(form.party_id, 10),
        against_id: parseInt(form.against_id, 10),
        amount: cashAmount,
        wht_percent: whtPercent,
        wht_amount: whtAmount,
        mode: form.mode,
        bank_name: form.mode === 'cheque' ? (form.bank_name || null) : null,
        cheque_number: form.mode === 'cheque' ? (form.cheque_number || null) : null,
        notes: form.notes.trim() || null,
      };

      if (form.mode === 'cheque') {
      if (!form.bank_name.trim()) return alert('❌ Bank Name is required for cheque payments.');
      if (!form.cheque_number.trim()) return alert('❌ Cheque Number is required for cheque payments.');
    }

      await chemicalService.createPayment(payload);
      const whtNote = whtAmount > 0 ? ` (incl. WHT Rs ${formatNumber(whtAmount)})` : '';
      alert(`✅ ${type === 'receipt' ? 'Receipt' : 'Payment'} recorded! Balance updated.${whtNote}`);
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save payment';
      alert('❌ ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  const partyLabel = type === 'receipt' ? 'Customer' : 'Vendor';
  const invoiceLabel = type === 'receipt' ? 'Invoice' : 'Bill';

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>
              {type === 'receipt' ? '↓ Record Receipt' : '↑ Record Payment'}
            </h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              {type === 'receipt'
                ? 'Record money received from a customer against an invoice'
                : 'Record money paid to a vendor against a bill'}
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px 30px' }}>

            {/* ─── Type Selector ────────────── */}
            <div style={styles.typeSwitchWrap}>
              <button
                type="button"
                onClick={() => setType('receipt')}
                style={{
                  ...styles.typeButton,
                  ...(type === 'receipt' ? styles.typeButtonActiveReceipt : {}),
                }}
              >
                ↓ Receipt (from customer)
              </button>
              <button
                type="button"
                onClick={() => setType('payment')}
                style={{
                  ...styles.typeButton,
                  ...(type === 'payment' ? styles.typeButtonActivePayment : {}),
                }}
              >
                ↑ Payment (to vendor)
              </button>
            </div>

            {/* ─── Party Picker ──────────────── */}
            <div style={styles.row}>
              <div style={{ ...styles.formGroup, flex: 2 }}>
                <label style={styles.label}>
                  {partyLabel} <span style={styles.required}>*</span>
                </label>
                <select
                  value={form.party_id}
                  onChange={(e) => handleChange('party_id', e.target.value)}
                  style={styles.input}
                  required
                  disabled={loadingParties}
                >
                  <option value="">
                    {loadingParties
                      ? `Loading ${partyLabel.toLowerCase()}s...`
                      : parties.length === 0
                        ? `-- No ${partyLabel.toLowerCase()}s with outstanding balance --`
                        : `-- Select ${partyLabel.toLowerCase()} --`}
                  </option>
                  {parties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} — Rs {formatNumber(p.outstanding_balance)} outstanding
                    </option>
                  ))}
                </select>
                {!loadingParties && parties.length === 0 && (
                  <span style={styles.hint}>
                    All {partyLabel.toLowerCase()}s are fully settled.
                  </span>
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Date <span style={styles.required}>*</span>
                </label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  style={styles.input}
                  required
                />
              </div>
            </div>

            {/* ─── Invoice/Bill Picker ──────── */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Against {invoiceLabel} <span style={styles.required}>*</span>
              </label>
              {!form.party_id ? (
                <input
                  style={{ ...styles.input, ...styles.inputDisabled }}
                  disabled
                  placeholder={`Select a ${partyLabel.toLowerCase()} first`}
                />
              ) : loadingInvoices ? (
                <input
                  style={{ ...styles.input, ...styles.inputDisabled }}
                  disabled
                  placeholder={`Loading ${invoiceLabel.toLowerCase()}s...`}
                />
              ) : invoices.length === 0 ? (
                <input
                  style={{ ...styles.input, borderColor: '#e74c3c' }}
                  disabled
                  value={`No unsettled ${invoiceLabel.toLowerCase()}s for this ${partyLabel.toLowerCase()}`}
                />
              ) : (
                <select
                  value={form.against_id}
                  onChange={(e) => handleChange('against_id', e.target.value)}
                  style={styles.input}
                  required
                >
                  <option value="">-- Select {invoiceLabel.toLowerCase()} --</option>
                  {invoices.map((inv) => {
                    const refNo = type === 'receipt' ? inv.invoice_no : inv.bill_no;
                    const dateField = type === 'receipt' ? inv.invoice_date : inv.purchase_date;
                    const remaining = parseFloat(inv.grand_total) - parseFloat(inv.amount_paid);
                    return (
                      <option key={inv.id} value={inv.id}>
                        {refNo} ({formatDate(dateField)}) — Rs {formatNumber(remaining)} remaining
                      </option>
                    );
                  })}
                </select>
              )}
            </div>

            {/* ─── Invoice details banner ──── */}
            {selectedInvoice && (
              <div style={styles.invoiceBanner}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px' }}>
                  <div>
                    <div style={styles.bannerLabel}>Total</div>
                    <strong>Rs {formatNumber(selectedInvoice.grand_total)}</strong>
                  </div>
                  <div>
                    <div style={styles.bannerLabel}>Already Paid</div>
                    <strong style={{ color: '#27ae60' }}>Rs {formatNumber(selectedInvoice.amount_paid)}</strong>
                  </div>
                  <div>
                    <div style={styles.bannerLabel}>Remaining</div>
                    <strong style={{ color: '#e74c3c' }}>Rs {formatNumber(remainingDue)}</strong>
                  </div>
                  <div>
                    <div style={styles.bannerLabel}>Status</div>
                    <span style={getStatusBadge(selectedInvoice.payment_status)}>
                      {formatStatus(selectedInvoice.payment_status)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* ─── WHT Section ─────────────── */}
            {selectedInvoice && (
              <div style={styles.whtSection}>
                <div style={styles.whtHeader}>
                  <span style={styles.whtTitle}>Withholding Tax (WHT)</span>
                  <span style={styles.whtSubtitle}>
                    {type === 'receipt' ? 'Tax deducted by customer before paying' : 'Tax deducted before paying vendor'}
                  </span>
                </div>
                <div style={styles.row}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>WHT %</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={form.wht_percent}
                      onChange={(e) => handleWhtChange(e.target.value)}
                      style={styles.input}
                      placeholder="e.g. 4"
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>WHT Amount (auto)</label>
                    <input
                      type="text"
                      value={whtAmount > 0 ? `Rs ${formatNumber(whtAmount)}` : ''}
                      style={{ ...styles.input, ...styles.inputDisabled }}
                      disabled
                      placeholder="Calculated from %"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ─── Amount & Mode ────────────── */}
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Cash {type === 'receipt' ? 'Received' : 'Paid'} (Rs) <span style={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  style={{
                    ...styles.input,
                    fontSize: '18px',
                    fontWeight: '700',
                    color: type === 'receipt' ? '#27ae60' : '#e74c3c',
                  }}
                  placeholder="0"
                  required
                  disabled={!selectedInvoice}
                />
                {selectedInvoice && whtAmount > 0 && (
                  <span style={styles.hint}>
                    Cash = Remaining (Rs {formatNumber(remainingDue)}) − WHT (Rs {formatNumber(whtAmount)})
                  </span>
                )}
                {selectedInvoice && whtAmount === 0 && (
                  <span style={styles.hint}>
                    Max Rs {formatNumber(remainingDue)} (remaining due)
                  </span>
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Mode <span style={styles.required}>*</span></label>
                <select
                  value={form.mode}
                  onChange={(e) => handleChange('mode', e.target.value)}
                  style={styles.input}
                  required
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m} value={m}>{formatPaymentMode(m)}</option>
                  ))}
                </select>
              </div>
            </div>

            {form.mode === 'cheque' && (
              <div style={styles.row}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Bank Name <span style={styles.required}>*</span></label>
                  <input
                    type="text"
                    value={form.bank_name}
                    onChange={(e) => handleChange('bank_name', e.target.value)}
                    style={styles.input}
                    placeholder="e.g. HBL, Meezan Bank"
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Cheque Number <span style={styles.required}>*</span></label>
                  <input
                    type="text"
                    value={form.cheque_number}
                    onChange={(e) => handleChange('cheque_number', e.target.value)}
                    style={styles.input}
                    placeholder="e.g. 123456"
                    required
                  />
                </div>
              </div>
            )}

            {/* ─── Settlement preview ──────── */}
            {selectedInvoice && (cashAmount > 0 || whtAmount > 0) && (
              <div style={styles.settlementPreview}>
                <div style={{ marginBottom: whtAmount > 0 ? '6px' : '0' }}>
                  {whtAmount > 0 ? (
                    <>
                      Cash {type === 'receipt' ? 'received' : 'paid'}: <strong>Rs {formatNumber(cashAmount)}</strong>
                      &nbsp;+ WHT deducted: <strong>Rs {formatNumber(whtAmount)}</strong>
                      &nbsp;= Total settled: <strong>Rs {formatNumber(totalSettlement)}</strong>
                    </>
                  ) : (
                    <>
                      After this transaction:&nbsp;
                      <strong>
                        Rs {formatNumber(parseFloat(selectedInvoice.amount_paid) + totalSettlement)}
                      </strong> paid of <strong>Rs {formatNumber(selectedInvoice.grand_total)}</strong>
                    </>
                  )}
                </div>
                {totalSettlement >= remainingDue - 0.01 ? (
                  <span style={{ color: '#27ae60', fontWeight: '700' }}>
                    → ✓ Fully Settled
                  </span>
                ) : (
                  <span style={{ color: '#856404', fontWeight: '700' }}>
                    → Partial (Rs {formatNumber(remainingDue - totalSettlement)} still remaining)
                  </span>
                )}
              </div>
            )}

            {/* ─── Notes ───────────────────── */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                style={{ ...styles.input, minHeight: '50px', resize: 'vertical', fontFamily: 'inherit' }}
                placeholder="e.g. Cheque #12345, deposited at HBL"
                rows="2"
              />
            </div>

          </div>

          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelButton} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...styles.submitButton,
                background: type === 'receipt' ? '#27ae60' : '#e74c3c',
              }}
              disabled={submitting || !selectedInvoice}
            >
              {submitting
                ? '⏳ Saving...'
                : type === 'receipt' ? '💾 Record Receipt' : '💾 Record Payment'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatNumber = (n) => {
  const num = parseFloat(n) || 0;
  return num.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const formatPaymentMode = (m) => {
  const map = {
    cash: 'Cash',
    cheque: 'Cheque',
  };
  return map[m] || m;
};

const formatStatus = (status) => {
  const map = { paid: '✓ Paid', partial: 'Partial', unpaid: 'Unpaid' };
  return map[status] || status;
};

const getStatusBadge = (status) => {
  const palette = {
    paid:    { bg: '#d4edda', fg: '#155724' },
    partial: { bg: '#fff3cd', fg: '#856404' },
    unpaid:  { bg: '#f8d7da', fg: '#721c24' },
  };
  const c = palette[status] || palette.unpaid;
  return {
    background: c.bg, color: c.fg,
    padding: '4px 10px', borderRadius: '12px',
    fontSize: '11px', fontWeight: '700',
    display: 'inline-block',
  };
};

// ─── Styles ───────────────────────────────────────────────

const styles = {
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  modal: {
    background: 'white', borderRadius: '12px',
    width: '95%', maxWidth: '720px', maxHeight: '92vh',
    overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '20px 30px', borderBottom: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    position: 'sticky', top: 0, background: 'white', zIndex: 10,
  },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: {
    padding: '8px 16px', background: '#f0f0f0', border: 'none',
    borderRadius: '8px', cursor: 'pointer',
    fontSize: '20px', fontWeight: '700', color: '#666',
  },

  typeSwitchWrap: {
    display: 'flex',
    gap: '0',
    marginBottom: '20px',
    border: '2px solid #e0e0e0',
    borderRadius: '10px',
    padding: '4px',
    background: '#f8f9fa',
  },
  typeButton: {
    flex: 1,
    padding: '10px 14px',
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '700',
    color: '#7f8c8d',
    transition: 'all 0.2s',
  },
  typeButtonActiveReceipt: {
    background: '#27ae60',
    color: 'white',
  },
  typeButtonActivePayment: {
    background: '#e74c3c',
    color: 'white',
  },

  row: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  formGroup: {
    flex: 1, minWidth: '180px',
    display: 'flex', flexDirection: 'column',
    gap: '6px', marginBottom: '14px',
  },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  required: { color: '#e74c3c' },
  input: {
    width: '100%', padding: '10px 12px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  inputDisabled: {
    background: '#f5f7fa', color: '#7f8c8d', cursor: 'not-allowed',
  },
  hint: {
    fontSize: '11px', color: '#95a5a6', fontStyle: 'italic',
  },

  invoiceBanner: {
    background: '#f8f9fa',
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    padding: '14px 16px',
    marginBottom: '14px',
    fontSize: '13px',
  },
  bannerLabel: {
    fontSize: '11px', color: '#7f8c8d', fontWeight: '600',
    textTransform: 'uppercase', letterSpacing: '0.3px',
    marginBottom: '4px',
  },

  whtSection: {
    background: '#fff8e1',
    border: '1px solid #ffe082',
    borderRadius: '10px',
    padding: '14px 16px',
    marginBottom: '14px',
  },
  whtHeader: {
    marginBottom: '10px',
  },
  whtTitle: {
    fontSize: '13px', fontWeight: '700', color: '#f57f17',
    display: 'block', marginBottom: '2px',
  },
  whtSubtitle: {
    fontSize: '11px', color: '#9e9d24',
  },

  settlementPreview: {
    background: '#fffbea',
    color: '#856404',
    padding: '10px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '14px',
    borderLeft: '4px solid #f39c12',
  },

  modalFooter: {
    padding: '16px 30px',
    borderTop: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'flex-end', gap: '12px',
    position: 'sticky', bottom: 0, background: 'white',
  },
  cancelButton: {
    padding: '12px 24px',
    background: '#95a5a6', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
  submitButton: {
    padding: '12px 28px',
    color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
};

export default PaymentFormModal;